import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { I18NextService } from 'angular-i18next';
import { TranslatedChecklist } from '../../../interfaces/checklists';
import { uniqueTasksValidator } from '../../../util/unique-tasks.validator';

const ZERO_LENGTH = 0;
@Component({
    selector: 'checklist-form',
    templateUrl: './checklist-form.component.html',
    styleUrls: ['./checklist-form.component.scss'],
})
export class ChecklistFormComponent implements OnInit {
    public checklistForm: FormGroup;
    public title: string;

    @Input() allowEditTitle = true;
    @Input() hasFlis = false;
    private _flis: INameId[];

    @Input() set flis(value: INameId[]) {
        this._flis = value ?? [];

        if (value != null && value.length > ZERO_LENGTH) {
            this.checklistForm?.get('flis').setValue(this.getSelectedFlis(value));
        }
    }

    get flis(): INameId[] {
        return this._flis;
    }

    private _checklist: TranslatedChecklist;
    @Input() set checklist(value: TranslatedChecklist) {
        if (value != null) {
            this.checklistForm?.get('name').setValue(value.name);

            this.tasks.clear();
            value.tasks.forEach((task) => this.addTask(task));

            this._checklist = value;

            if (this.hasFlis) {
                this.checklistForm?.get('flis').setValue(this.getSelectedFlis());
            }

            this.title = value.name;
        } else {
            this.title = this.i18n.t('aumico_reports_create_new_checklist') as string;
            this.addTask();
        }
    }

    get checklist(): TranslatedChecklist {
        return this._checklist;
    }

    @Output() onSubmitChecklistForm = new EventEmitter<Partial<TranslatedChecklist>>();
    @Output() onCancelCheckList = new EventEmitter<void>();
    @Output() markTaskToRemove = new EventEmitter<string>();

    constructor(private readonly fb: FormBuilder, private i18n: I18NextService) {
        this.checklistForm = this.fb.group({
            name: [''],
            tasks: this.fb.array([], uniqueTasksValidator()),
            flis: [[]],
        });

        this.title = this.i18n.t('aumico_reports_create_new_checklist') as string;
    }

    ngOnInit(): void {
        if (this.hasFlis) {
            this.checklistForm.get('flis').setValidators(Validators.required);
        }

        if (this.allowEditTitle) {
            this.checklistForm.get('name').setValidators(Validators.required);
        }
    }

    get tasks(): FormArray {
        return this.checklistForm.get('tasks') as FormArray;
    }

    addTask(task?: string): void {
        this.tasks.push(this.fb.control(task, Validators.required));
    }

    removeTask(index: number): void {
        this.tasks.removeAt(index);
        this.markTaskToRemove.emit(this.checklist.tasks[index]);
        this.checklist.tasks.splice(index, 1);
    }

    onSubmit(): void {
        const { name, tasks, flis } = this.checklistForm.value;
        const flisIds = flis?.map((fli) => fli);

        let checklist: Partial<TranslatedChecklist>;

        if (this.checklist != null) {
            checklist = {
                ...this.checklist,
                name,
                tasks,
                flis: flisIds,
            };
        } else {
            checklist = {
                name,
                tasks,
                flis: flisIds,
            };
        }
        this.onSubmitChecklistForm.emit(checklist);
    }

    onCancel(): void {
        this.onCancelCheckList.emit();
    }

    private getSelectedFlis(value?: INameId[]): INameId[] {
        let allFlis = value;
        if (value && value.length == ZERO_LENGTH) {
            return [];
        } else {
            if (this.flis && this.flis?.length == ZERO_LENGTH) {
                return [];
            }
            allFlis = this.flis;
        }
        if (allFlis === undefined) {
            return [];
        }

        const selectedFlis: INameId[] = [];
        this.checklist?.flis.forEach((checkedFli) => {
            const selectedFli = allFlis.find((fli) => fli.id === checkedFli);
            if (selectedFli != null) {
                selectedFlis.push(selectedFli);
            }
        });

        return selectedFlis;
    }
}
